import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Search,
  Download,
  RefreshCw,
  LogOut,
  Mail,
  MessageSquare,
  Users,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "operatorai_admin_token";

const fetchJson = async (path, token) => {
  const res = await fetch(`${API}${path}`, {
    headers: { Authorization: `Basic ${token}` },
  });
  if (res.status === 401) throw new Error("UNAUTHORIZED");
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  return res.json();
};

const downloadCsv = async (path, filename, token) => {
  const res = await fetch(`${API}${path}`, {
    headers: { Authorization: `Basic ${token}` },
  });
  if (!res.ok) throw new Error("Export failed");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// ---------- Login ----------

const Login = ({ onSubmit, error, loading }) => {
  const [u, setU] = useState("admin");
  const [p, setP] = useState("");
  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(u, p);
        }}
        data-testid="admin-login-form"
        className="w-full max-w-sm"
      >
        <div className="flex items-center gap-2 mb-8">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-[#0A0A0A] text-white font-extrabold text-sm tracking-tight">
            O
          </span>
          <span className="font-extrabold tracking-tight text-lg text-[#0A0A0A]">
            OperatorAI Admin
          </span>
        </div>
        <h1 className="text-3xl font-extrabold tracking-tighter text-[#0A0A0A]">
          Sign in
        </h1>
        <p className="text-sm text-[#525252] mt-2 mb-8">
          Internal lead dashboard for OperatorAI.
        </p>

        <label className="block text-sm font-semibold text-[#0A0A0A] mb-2">
          Username
        </label>
        <input
          data-testid="admin-username-input"
          type="text"
          value={u}
          onChange={(e) => setU(e.target.value)}
          autoComplete="username"
          className="w-full bg-white border border-[#E5E5E5] rounded-lg px-4 py-3 text-[#0A0A0A] outline-none focus:border-[#0052FF] focus:ring-2 focus:ring-[#0052FF]/20 transition-all mb-4"
        />

        <label className="block text-sm font-semibold text-[#0A0A0A] mb-2">
          Password
        </label>
        <input
          data-testid="admin-password-input"
          type="password"
          value={p}
          onChange={(e) => setP(e.target.value)}
          autoComplete="current-password"
          className="w-full bg-white border border-[#E5E5E5] rounded-lg px-4 py-3 text-[#0A0A0A] outline-none focus:border-[#0052FF] focus:ring-2 focus:ring-[#0052FF]/20 transition-all"
        />

        {error && (
          <p
            data-testid="admin-login-error"
            className="mt-4 text-sm font-medium text-red-600"
          >
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="mt-6 w-full inline-flex items-center justify-center bg-[#0052FF] hover:bg-[#0040CC] disabled:opacity-70 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
        >
          {loading ? "Signing in…" : "Sign in"}
        </button>

        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-1.5 text-xs font-medium text-[#525252] hover:text-[#0A0A0A]"
        >
          <ArrowLeft size={12} /> Back to operatoraihq.com
        </Link>
      </form>
    </div>
  );
};

// ---------- Stat card ----------

const Stat = ({ icon: Icon, label, value }) => (
  <div className="bg-white border border-[#E5E5E5] rounded-xl p-5 flex items-center gap-4">
    <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[#0052FF]/10 text-[#0052FF]">
      <Icon size={18} />
    </span>
    <div>
      <div className="text-xs uppercase tracking-wider font-bold text-[#525252]">
        {label}
      </div>
      <div className="text-xl font-extrabold text-[#0A0A0A] leading-tight">
        {value}
      </div>
    </div>
  </div>
);

// ---------- Dashboard ----------

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const Dashboard = ({ token, onLogout }) => {
  const [tab, setTab] = useState("subscribers");
  const [q, setQ] = useState("");
  const [includeUnsub, setIncludeUnsub] = useState(false);
  const [subs, setSubs] = useState({ items: [], total: 0, active: 0, unsubscribed: 0 });
  const [contacts, setContacts] = useState({ items: [], total: 0 });
  const [sources, setSources] = useState({ channels: [], campaigns: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const subsQS = new URLSearchParams();
      if (q) subsQS.set("q", q);
      if (includeUnsub) subsQS.set("include_unsubscribed", "true");
      const subsPath = `/admin/subscribers${subsQS.toString() ? `?${subsQS}` : ""}`;
      const contactsPath = `/admin/contacts${q ? `?q=${encodeURIComponent(q)}` : ""}`;
      const [s, c, src] = await Promise.all([
        fetchJson(subsPath, token),
        fetchJson(contactsPath, token),
        fetchJson("/admin/sources", token),
      ]);
      setSubs(s);
      setContacts(c);
      setSources(src);
    } catch (e) {
      if (e.message === "UNAUTHORIZED") {
        onLogout();
        return;
      }
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced search + filter
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, includeUnsub]);

  const exportCsv = async () => {
    const path =
      tab === "subscribers"
        ? "/admin/export/subscribers.csv"
        : "/admin/export/contacts.csv";
    const filename =
      tab === "subscribers"
        ? "operatorai-subscribers.csv"
        : "operatorai-contacts.csv";
    try {
      await downloadCsv(path, filename, token);
    } catch (e) {
      setErr(e.message);
    }
  };

  const activeRows = useMemo(
    () => (tab === "subscribers" ? subs.items : contacts.items),
    [tab, subs, contacts]
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#0A0A0A]">
      <header className="bg-white border-b border-[#E5E5E5] sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-[#0A0A0A] text-white font-extrabold text-sm tracking-tight">
              O
            </span>
            <span className="font-extrabold tracking-tight text-lg">
              OperatorAI <span className="text-[#0052FF]">/ Admin</span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-1.5 text-xs font-semibold py-2 px-3 rounded-md border border-[#E5E5E5] hover:border-[#0A0A0A]/30 transition-colors"
            >
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-1.5 text-xs font-semibold py-2 px-3 rounded-md border border-[#E5E5E5] hover:border-[#0A0A0A]/30 transition-colors"
            >
              <LogOut size={13} />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 sm:px-8 py-8 sm:py-10">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <Stat icon={Users} label="Active subscribers" value={subs.active ?? subs.total} />
          <Stat icon={MessageSquare} label="Total contacts" value={contacts.total} />
          <Stat
            icon={Mail}
            label="Unsubscribed"
            value={subs.unsubscribed ?? 0}
          />
        </div>

        {/* Source attribution rollup */}
        <div
          data-testid="admin-sources-card"
          className="bg-white border border-[#E5E5E5] rounded-2xl p-6 mb-8"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#0052FF]">
                Attribution
              </div>
              <h2 className="text-lg font-bold text-[#0A0A0A] mt-1">Top channels</h2>
            </div>
            <div className="text-xs text-[#525252]">
              Subscribers grouped by <code className="text-[#0A0A0A]">utm_source</code>
            </div>
          </div>

          {sources.channels.length === 0 ? (
            <div className="text-sm text-[#9CA3AF] py-6 text-center">
              No subscribers yet — add a UTM-tagged link in your social bios to start
              collecting attribution.
            </div>
          ) : (
            <div className="space-y-2">
              {sources.channels.map((c) => {
                const pct =
                  sources.total > 0 ? Math.round((c.count / sources.total) * 100) : 0;
                return (
                  <div
                    key={c.channel}
                    data-testid={`admin-source-row-${c.channel}`}
                    className="flex items-center gap-3"
                  >
                    <div className="w-28 sm:w-36 text-sm font-semibold text-[#0A0A0A] capitalize">
                      {c.channel}
                    </div>
                    <div className="flex-1 h-2 bg-[#FAFAFA] rounded-full overflow-hidden border border-[#E5E5E5]">
                      <div
                        className="h-full bg-[#0052FF]"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="w-20 text-right text-sm font-mono text-[#0A0A0A]">
                      {c.count}
                    </div>
                    <div className="w-12 text-right text-xs font-mono text-[#525252]">
                      {pct}%
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {sources.campaigns.length > 0 && (
            <div className="mt-6 pt-5 border-t border-[#E5E5E5]">
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#525252] mb-3">
                Active campaigns
              </div>
              <div className="flex flex-wrap gap-2">
                {sources.campaigns.map((c) => (
                  <span
                    key={c.campaign}
                    className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-md bg-[#FAFAFA] border border-[#E5E5E5]"
                  >
                    <span className="text-[#0A0A0A]">{c.campaign}</span>
                    <span className="text-[#525252] font-mono">{c.count}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-5 border-b border-[#E5E5E5]">
            <div className="flex items-center gap-1 bg-[#FAFAFA] border border-[#E5E5E5] rounded-lg p-1">
              <button
                onClick={() => setTab("subscribers")}
                data-testid="admin-tab-subscribers"
                className={`text-sm font-semibold px-4 py-1.5 rounded-md transition-colors ${
                  tab === "subscribers"
                    ? "bg-white text-[#0A0A0A] shadow-sm"
                    : "text-[#525252] hover:text-[#0A0A0A]"
                }`}
              >
                Subscribers ({subs.active ?? subs.total})
              </button>
              <button
                onClick={() => setTab("contacts")}
                data-testid="admin-tab-contacts"
                className={`text-sm font-semibold px-4 py-1.5 rounded-md transition-colors ${
                  tab === "contacts"
                    ? "bg-white text-[#0A0A0A] shadow-sm"
                    : "text-[#525252] hover:text-[#0A0A0A]"
                }`}
              >
                Contacts ({contacts.total})
              </button>
            </div>

            <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap">
              {tab === "subscribers" && (
                <label
                  data-testid="admin-include-unsub-toggle"
                  className="inline-flex items-center gap-2 text-xs font-semibold text-[#525252] cursor-pointer select-none px-3 py-2 rounded-lg border border-[#E5E5E5] hover:border-[#0A0A0A]/30 transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={includeUnsub}
                    onChange={(e) => setIncludeUnsub(e.target.checked)}
                    className="accent-[#0052FF]"
                  />
                  Show unsubscribed
                </label>
              )}
              <div className="relative flex-1 sm:flex-initial">
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]"
                />
                <input
                  data-testid="admin-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search email, name, message…"
                  className="w-full sm:w-72 bg-white border border-[#E5E5E5] rounded-lg pl-9 pr-3 py-2 text-sm outline-none focus:border-[#0052FF] focus:ring-2 focus:ring-[#0052FF]/20 transition-all"
                />
              </div>
              <button
                onClick={exportCsv}
                data-testid="admin-export-csv"
                className="inline-flex items-center gap-1.5 text-sm font-semibold py-2 px-3 rounded-lg bg-[#0A0A0A] text-white hover:bg-[#1F1F1F] transition-colors"
              >
                <Download size={14} />
                CSV
              </button>
            </div>
          </div>

          {err && (
            <div className="p-4 bg-red-50 border-b border-red-200 text-sm text-red-700">
              {err}
            </div>
          )}

          <div className="overflow-x-auto" data-testid={`admin-table-${tab}`}>
            {tab === "subscribers" ? (
              <table className="w-full text-sm">
                <thead className="bg-[#FAFAFA] text-[#525252] uppercase tracking-wider text-xs">
                  <tr>
                    <th className="text-left font-bold py-3 px-5">Email</th>
                    <th className="text-left font-bold py-3 px-5">Channel</th>
                    <th className="text-left font-bold py-3 px-5">Campaign</th>
                    <th className="text-left font-bold py-3 px-5">Form</th>
                    <th className="text-left font-bold py-3 px-5">Status</th>
                    <th className="text-left font-bold py-3 px-5">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {activeRows.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center py-12 text-[#9CA3AF]">
                        {loading ? "Loading…" : "No subscribers yet."}
                      </td>
                    </tr>
                  )}
                  {activeRows.map((r, i) => (
                    <tr
                      key={r.id || i}
                      className="border-t border-[#E5E5E5] hover:bg-[#FAFAFA]"
                    >
                      <td className="py-3 px-5 font-medium">{r.email}</td>
                      <td className="py-3 px-5 text-[#0A0A0A] capitalize">
                        {r.utm_source || (
                          <span className="text-[#9CA3AF]">direct</span>
                        )}
                      </td>
                      <td className="py-3 px-5 text-[#525252]">
                        {r.utm_campaign || "—"}
                      </td>
                      <td className="py-3 px-5 text-[#525252]">{r.source || "—"}</td>
                      <td className="py-3 px-5">
                        {r.unsubscribed ? (
                          <span className="inline-flex items-center text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-md bg-red-50 text-red-700 border border-red-100">
                            Unsubscribed
                          </span>
                        ) : (
                          <span className="inline-flex items-center text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-md bg-[#0052FF]/10 text-[#0052FF]">
                            Active
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-5 text-[#525252] whitespace-nowrap">
                        {fmt(r.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-[#FAFAFA] text-[#525252] uppercase tracking-wider text-xs">
                  <tr>
                    <th className="text-left font-bold py-3 px-5">Name</th>
                    <th className="text-left font-bold py-3 px-5">Email</th>
                    <th className="text-left font-bold py-3 px-5">Message</th>
                    <th className="text-left font-bold py-3 px-5">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {activeRows.length === 0 && (
                    <tr>
                      <td colSpan={4} className="text-center py-12 text-[#9CA3AF]">
                        {loading ? "Loading…" : "No contact messages yet."}
                      </td>
                    </tr>
                  )}
                  {activeRows.map((r, i) => (
                    <tr
                      key={r.id || i}
                      className="border-t border-[#E5E5E5] hover:bg-[#FAFAFA] align-top"
                    >
                      <td className="py-3 px-5 font-medium">{r.name}</td>
                      <td className="py-3 px-5">
                        <a
                          href={`mailto:${r.email}`}
                          className="text-[#0052FF] hover:underline"
                        >
                          {r.email}
                        </a>
                      </td>
                      <td className="py-3 px-5 text-[#374151] max-w-md whitespace-pre-wrap">
                        {r.message}
                      </td>
                      <td className="py-3 px-5 text-[#525252] whitespace-nowrap">
                        {fmt(r.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

// ---------- Page wrapper ----------

export const Admin = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (username, password) => {
    setError("");
    setLoading(true);
    const candidate = btoa(`${username}:${password}`);
    try {
      await fetchJson("/admin/me", candidate);
      sessionStorage.setItem(STORAGE_KEY, candidate);
      setToken(candidate);
    } catch (e) {
      setError(
        e.message === "UNAUTHORIZED"
          ? "Wrong username or password."
          : "Could not sign in. Check your connection."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setToken("");
  };

  if (!token) {
    return <Login onSubmit={handleLogin} error={error} loading={loading} />;
  }
  return <Dashboard token={token} onLogout={handleLogout} />;
};

export default Admin;
